/**
 * BoatMeshGenerator.ts — single source of procedural boat parts.
 *
 * Goal of this module (Etap 7B): generate all hull parts that depend on
 * `BoatAnchorPoints` so positions in the rendered mesh, the physics, and
 * the future mooring system stay in sync. Big surfaces (lofted hull,
 * cabin box, lifelines, fenders, sails) still live in
 * `BoatRenderPass.buildBoatMesh`. This module owns the Antila-specific
 * hardware that has no other home: cleats, tiller, outboard engine,
 * swing-up centerboard, and the transom accent.
 *
 * Conventions:
 *   - Body frame: +X forward, +Y up, +Z starboard (project standard).
 *   - Vertex colors only (no UVs / textures). Faceted normals.
 *
 * See `antila-28-2-model-prompt.md` for the full target spec.
 */

import { createBox } from "../../assets/ProceduralAssets";
import { MESH_VERTEX_STRIDE } from "../../assets/MeshAsset";
import type { MeshAsset } from "../../assets/MeshAsset";
import type { BoatAnchorPoints } from "./BoatAnchorPoints";
import type { BoatHull } from "../BoatHull";

/** A single transformed sub-mesh ready to be appended to `parts[]`. */
export interface BoatPart {
  mesh: MeshAsset;
  transform?: (p: [number, number, number]) => [number, number, number];
}

// =====================================================================
// Color palette (Antila 28.2 — see spec §"Paleta kolorów")
// =====================================================================
const STEEL_GREY: [number, number, number] = [0.78, 0.78, 0.82];
const ANTIFOULING: [number, number, number] = [0.118, 0.227, 0.31]; // #1E3A4F
const TILLER_WOOD: [number, number, number] = [0.36, 0.227, 0.157]; // #5C3A28
const ENGINE_BLACK: [number, number, number] = [0.165, 0.165, 0.165]; // #2A2A2A
const PROPELLER_SILVER: [number, number, number] = [0.75, 0.75, 0.78];

// =====================================================================
// Cleats — T-shaped fittings on deck.
// =====================================================================

const CLEAT_DEFAULT_COLOR: [number, number, number] = STEEL_GREY;

export interface CleatStyle {
  height?: number;
  armLength?: number;
  thickness?: number;
  color?: [number, number, number];
}

export function generateCleats(
  anchors: BoatAnchorPoints,
  style: CleatStyle = {},
): BoatPart[] {
  const h = style.height ?? 0.06;
  const arm = style.armLength ?? 0.18;
  const t = style.thickness ?? 0.025;
  const color = style.color ?? CLEAT_DEFAULT_COLOR;

  const out: BoatPart[] = [];
  for (const c of anchors.cleats) {
    const [cx, cy, cz] = c.position;
    out.push({
      mesh: createBox(t, h, t, color, "cleat_post"),
      transform: ([px, py, pz]) => [px + cx, py + cy + h * 0.5, pz + cz],
    });
    out.push({
      mesh: createBox(arm, t, t, color, "cleat_arm"),
      transform: ([px, py, pz]) => [px + cx, py + cy + h, pz + cz],
    });
  }
  return out;
}

// =====================================================================
// Cabin trunk — raked-front, slight tumblehome sides, flat top.
// Replaces the trivial axis-aligned box for sailboats.
// =====================================================================

export interface CabinSpec {
  length: number;
  width: number;
  height: number;
  offsetX: number;
  color: [number, number, number];
}

/**
 * Build a faceted cabin trunk mesh:
 *   - front bulkhead raked aft by `frontRakeRad` (Antila ~22°)
 *   - rear (companionway) raked forward by `rearRakeRad`
 *   - sides slightly tumbled inward (top narrower than base)
 *   - flat top, no bottom (sits on deck)
 *
 * Coordinates: cabin sits on deck at Y = freeboard, body frame +X forward.
 */
export function generateCabinTrunk(
  cabin: CabinSpec,
  freeboard: number,
  frontRakeRad: number = (22 * Math.PI) / 180,
  rearRakeRad: number = (12 * Math.PI) / 180,
): BoatPart[] {
  const L = cabin.length;
  const halfW = cabin.width * 0.5;
  const H = cabin.height;
  const cx = cabin.offsetX;
  const yB = freeboard;
  const yT = freeboard + H;
  const xF = cx + L * 0.5;
  const xB = cx - L * 0.5;
  const frontRake = Math.tan(frontRakeRad) * H;
  const rearRake = Math.tan(rearRakeRad) * H;
  const xFt = xF - frontRake; // top-front shifted aft
  const xBt = xB + rearRake; // top-rear shifted fwd
  // Antila has near-vertical cabin sides — no tumblehome.
  const halfWt = halfW;

  type P = readonly [number, number, number];
  const BFP: P = [xF, yB, -halfW];
  const BFS: P = [xF, yB, +halfW];
  const BBP: P = [xB, yB, -halfW];
  const BBS: P = [xB, yB, +halfW];
  const TFP: P = [xFt, yT, -halfWt];
  const TFS: P = [xFt, yT, +halfWt];
  const TBP: P = [xBt, yT, -halfWt];
  const TBS: P = [xBt, yT, +halfWt];

  const verts: number[] = [];
  const idx: number[] = [];
  const c = cabin.color;
  const addQuad = (a: P, b: P, c2: P, d: P) => {
    // Faceted normal from CCW (a, b, c2). Outward by construction.
    const e1x = b[0] - a[0],
      e1y = b[1] - a[1],
      e1z = b[2] - a[2];
    const e2x = c2[0] - a[0],
      e2y = c2[1] - a[1],
      e2z = c2[2] - a[2];
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    const ux = nx / len,
      uy = ny / len,
      uz = nz / len;
    const base = verts.length / 9;
    for (const p of [a, b, c2, d]) {
      verts.push(p[0], p[1], p[2], ux, uy, uz, c[0], c[1], c[2]);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  // Faces wound CCW so cross-product normals point outward.
  // Front (+X out): bottom-port → top-port → top-stbd → bottom-stbd
  addQuad(BFP, TFP, TFS, BFS);
  // Rear (-X out): mirror
  addQuad(BBS, TBS, TBP, BBP);
  // Port (-Z out)
  addQuad(BBP, TBP, TFP, BFP);
  // Starboard (+Z out)
  addQuad(BFS, TFS, TBS, BBS);
  // Top (+Y out)
  addQuad(TFP, TBP, TBS, TFS);

  const mesh: MeshAsset = {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label: "cabin_trunk",
  };
  return [{ mesh }];
}

// =====================================================================
// Companionway — dark rectangular opening on the raked rear bulkhead.
// =====================================================================

const COMPANIONWAY_DARK: [number, number, number] = [0.06, 0.06, 0.07];

/**
 * Place a dark panel flush against the cabin's raked rear face, sized
 * ~50 % cabin width, ~70 % cabin height, slightly recessed for depth.
 */
export function generateCompanionway(
  cabin: CabinSpec,
  freeboard: number,
  rearRakeRad: number = (12 * Math.PI) / 180,
): BoatPart[] {
  const w = cabin.width * 0.5;
  const h = cabin.height * 0.7;
  const thk = 0.04;
  const tan = Math.tan(rearRakeRad);
  // Y centre slightly above deck, top almost reaches cabin top.
  const yC = freeboard + cabin.height * 0.42;
  // X position: rear face at this Y. Rear face is xB at deck, slopes
  // forward by tan*y above deck. Recess ~3 cm into the cabin.
  const yAbove = yC - freeboard;
  const xRear = cabin.offsetX - cabin.length * 0.5 + tan * yAbove + 0.03;
  const panel = createBox(thk, h, w, COMPANIONWAY_DARK, "companionway");
  return [
    {
      mesh: panel,
      transform: ([x, y, z]) => [x + xRear, y + yC, z],
    },
  ];
}

// =====================================================================
// Cockpit — recessed sole + two longitudinal benches.
// =====================================================================

const COCKPIT_FLOOR: [number, number, number] = [0.42, 0.39, 0.34]; // grey
const COCKPIT_SEAT: [number, number, number] = [0.72, 0.65, 0.52]; // teak

/**
 * Build the cockpit interior: a sole (floor) ~15 cm below deck, plus two
 * longitudinal teak benches port and starboard. Spans from the rear of
 * the cabin to ~85 % of the way to the stern, ~60 % beam wide.
 */
export function generateCockpit(
  cabin: CabinSpec,
  hullLen: number,
  hullBeam: number,
  freeboard: number,
): BoatPart[] {
  const out: BoatPart[] = [];
  // Cockpit X-range: from cabin rear to short of stern.
  const xRearCabin = cabin.offsetX - cabin.length * 0.5;
  const xCockpitFwd = xRearCabin - 0.05;
  const xCockpitAft = -hullLen * 0.5 + hullLen * 0.08;
  const cockpitLen = xCockpitFwd - xCockpitAft;
  if (cockpitLen < 0.5) return out;
  const cockpitCx = (xCockpitFwd + xCockpitAft) * 0.5;
  // Width: 60 % beam. Sole sits ~15 cm below deck.
  const cockpitWid = hullBeam * 0.6;
  const soleY = freeboard - 0.18;
  const soleThk = 0.04;
  out.push({
    mesh: createBox(
      cockpitLen,
      soleThk,
      cockpitWid,
      COCKPIT_FLOOR,
      "cockpit_sole",
    ),
    transform: ([x, y, z]) => [x + cockpitCx, y + soleY, z],
  });
  // Two longitudinal benches: one per side, hugging the inboard edge of
  // the sole so they can never poke outside a narrowing aft hull.
  const seatLen = cockpitLen * 0.92;
  const seatHei = 0.18;
  const seatWid = hullBeam * 0.16;
  const seatY = freeboard - seatHei * 0.5;
  for (const sign of [-1, +1] as const) {
    const seatCz = sign * (cockpitWid * 0.5 - seatWid * 0.5);
    out.push({
      mesh: createBox(seatLen, seatHei, seatWid, COCKPIT_SEAT, "cockpit_seat"),
      transform: ([x, y, z]) => [x + cockpitCx, y + seatY, z + seatCz],
    });
  }
  return out;
}

// =====================================================================
// Foredeck hatch — small low box on the deck forward of the cabin.
// =====================================================================

const HATCH_DARK: [number, number, number] = [0.16, 0.18, 0.2];

export function generateForedeckHatch(
  cabin: CabinSpec,
  freeboard: number,
): BoatPart[] {
  const w = 0.55;
  const l = 0.55;
  const h = 0.05;
  const xC = cabin.offsetX + cabin.length * 0.5 + 0.6;
  const yC = freeboard + h * 0.5;
  return [
    {
      mesh: createBox(l, h, w, HATCH_DARK, "foredeck_hatch"),
      transform: ([x, y, z]) => [x + xC, y + yC, z],
    },
  ];
}

/**
 * Generate the rudder tiller: a thin wooden bar from `rudderPivot`
 * forward and slightly up to `tillerHandle`. Yaws with the rudder angle
 * around the rudder pivot's vertical axis.
 */
export function generateTiller(
  anchors: BoatAnchorPoints,
  rudderAngleRad = 0,
  thickness = 0.04,
): BoatPart[] {
  if (!anchors.tillerHandle) return [];
  const [px, py, pz] = anchors.rudderPivot;
  const [hx, hy, hz] = anchors.tillerHandle;
  const dx0 = hx - px;
  const dy = hy - py;
  const dz0 = hz - pz;
  const cy = Math.cos(rudderAngleRad);
  const sy = Math.sin(rudderAngleRad);
  const dx = dx0 * cy - dz0 * sy;
  const dz = dx0 * sy + dz0 * cy;
  const horiz = Math.hypot(dx, dz);
  const len = Math.hypot(dx, dy, dz);
  if (len < 0.05) return [];

  const yaw = Math.atan2(dz, dx);
  const pitch = Math.atan2(dy, horiz);
  const cYaw = Math.cos(yaw);
  const sYaw = Math.sin(yaw);
  const cPit = Math.cos(pitch);
  const sPit = Math.sin(pitch);

  const cx = px + dx * 0.5;
  const ccy = py + dy * 0.5;
  const cz = pz + dz * 0.5;

  const bar = createBox(len, thickness, thickness, TILLER_WOOD, "tiller");
  return [
    {
      mesh: bar,
      transform: ([qx, qy, qz]) => {
        const x1 = qx * cPit - qy * sPit;
        const y1 = qx * sPit + qy * cPit;
        const z1 = qz;
        const x2 = x1 * cYaw - z1 * sYaw;
        const z2 = x1 * sYaw + z1 * cYaw;
        return [cx + x2, ccy + y1, cz + z2];
      },
    },
  ];
}

// =====================================================================
// Outboard engine — body box + propeller housing + 3-blade prop.
// Mounted at engineMount, yaws with steering when linkage is active.
// =====================================================================

export interface OutboardEngineOptions {
  /** Yaw angle of the engine (rad). 0 = aligned with hull. */
  steeringAngleRad?: number;
  /** Rotation of the propeller blades (rad), animated by throttle. */
  propellerSpinRad?: number;
}

/**
 * Outboard engine: rectangular body above the transom, narrow shaft
 * descending to a torpedo housing, 3-blade propeller. All parts pivot
 * together around the engine_mount Y-axis.
 */
export function generateOutboardEngine(
  anchors: BoatAnchorPoints,
  opts: OutboardEngineOptions = {},
): BoatPart[] {
  const steer = opts.steeringAngleRad ?? 0;
  const spin = opts.propellerSpinRad ?? 0;
  const cs = Math.cos(steer);
  const ss = Math.sin(steer);
  const [mx, my, mz] = anchors.engineMount;

  // Rotate a body-frame offset around the mount's vertical axis.
  const place = (
    dx: number,
    dy: number,
    dz: number,
  ): [number, number, number] => {
    const rx = dx * cs - dz * ss;
    const rz = dx * ss + dz * cs;
    return [mx + rx, my + dy, mz + rz];
  };

  // Body sits above the mount.
  const bodyLen = 0.55;
  const bodyHei = 0.65;
  const bodyWid = 0.28;
  const bodyCenter = place(0, bodyHei * 0.5 + 0.1, 0);

  // Shaft: thin vertical box descending from body to housing.
  const shaftLen = 0.5;
  const shaftCenter = place(0, -shaftLen * 0.5 - 0.05, 0);

  // Propeller housing centred slightly aft of the prop position so the
  // torpedo extends back through the prop.
  const housingLen = 0.34;
  const housingDia = 0.15;
  let propPos: [number, number, number];
  if (anchors.propellerPos) {
    const [px, py, pz] = anchors.propellerPos;
    const ldx = px - mx;
    const ldy = py - my;
    const ldz = pz - mz;
    const rx = ldx * cs - ldz * ss;
    const rz = ldx * ss + ldz * cs;
    propPos = [mx + rx, my + ldy, mz + rz];
  } else {
    propPos = place(-0.35, -0.55, 0);
  }
  const housingCenter: [number, number, number] = [
    propPos[0] + housingLen * 0.5 * cs,
    propPos[1],
    propPos[2] + housingLen * 0.5 * ss,
  ];

  const out: BoatPart[] = [];

  // Build a yaw-rotation transform that places a unit-centred box at a
  // given world centre, with engine-frame +X aligned with the boat's
  // forward axis rotated by `steer`.
  const yawTransform = (centre: [number, number, number]) => {
    const [ccx, ccy, ccz] = centre;
    return ([qx, qy, qz]: [number, number, number]): [
      number,
      number,
      number,
    ] => {
      const rx = qx * cs - qz * ss;
      const rz = qx * ss + qz * cs;
      return [ccx + rx, ccy + qy, ccz + rz];
    };
  };

  out.push({
    mesh: createBox(bodyLen, bodyHei, bodyWid, ENGINE_BLACK, "engine_body"),
    transform: yawTransform(bodyCenter),
  });
  out.push({
    mesh: createBox(0.08, shaftLen, 0.08, ENGINE_BLACK, "engine_shaft"),
    transform: yawTransform(shaftCenter),
  });
  out.push({
    mesh: createBox(
      housingLen,
      housingDia,
      housingDia,
      ANTIFOULING,
      "prop_housing",
    ),
    transform: yawTransform(housingCenter),
  });

  // 3-blade propeller. Each blade is a thin radial box rotated by phase
  // around the engine's forward axis (+X after yaw).
  const bladeLen = 0.22;
  const bladeWid = 0.06;
  const bladeThk = 0.012;
  for (let i = 0; i < 3; i++) {
    const phase = (i / 3) * Math.PI * 2 + spin;
    const cy = Math.cos(phase);
    const sy = Math.sin(phase);
    const blade = createBox(
      bladeWid,
      bladeLen,
      bladeThk,
      PROPELLER_SILVER,
      "prop_blade",
    );
    const cxBlade = propPos[0];
    const cyBlade = propPos[1] + cy * bladeLen * 0.5;
    const czBlade = propPos[2] + sy * bladeLen * 0.5 * cs;
    const cxOff = -sy * bladeLen * 0.5 * ss;
    out.push({
      mesh: blade,
      transform: ([qx, qy, qz]) => {
        // Rotate around engine forward axis (X): in YZ plane.
        const y1 = qy * cy - qz * sy;
        const z1 = qy * sy + qz * cy;
        // Apply engine yaw around world Y.
        const x2 = qx * cs - z1 * ss;
        const z2 = qx * ss + z1 * cs;
        return [cxBlade + x2 + cxOff, cyBlade + y1, czBlade + z2];
      },
    });
  }

  return out;
}

// =====================================================================
// Centerboard (swing-up miecz) — pivots in trunk, deploys downward.
// =====================================================================

/**
 * Generate the centerboard blade: pivots around `centerboardPivot` and
 * sweeps from "up" (retracted into trunk, ~75° forward) to "down"
 * (vertical, full draft).
 *
 * @param extension 0..1, where 0 = retracted, 1 = full deployment.
 */
export function generateCenterboard(
  anchors: BoatAnchorPoints,
  hull: BoatHull,
  extension = 1,
): BoatPart[] {
  if (!anchors.centerboardPivot) return [];
  void hull;
  const [px, py, pz] = anchors.centerboardPivot;
  const chord = 0.4;
  const thickness = 0.06;
  const bladeLen = 1.35;
  const e = Math.max(0, Math.min(1, extension));
  const swingDeg = -75 * (1 - e);
  const swing = (swingDeg * Math.PI) / 180;
  const cs = Math.cos(swing);
  const ss = Math.sin(swing);
  const halfLen = bladeLen * 0.5;
  const cx = px + halfLen * ss;
  const cy = py - halfLen * cs;
  const cz = pz;
  const blade = createBox(
    thickness,
    bladeLen,
    chord,
    ANTIFOULING,
    "centerboard",
  );
  return [
    {
      mesh: blade,
      transform: ([qx, qy, qz]) => {
        const x1 = qx * cs - qy * ss;
        const y1 = qx * ss + qy * cs;
        return [cx + x1, cy + y1, cz + qz];
      },
    },
  ];
}

// =====================================================================
// Transom — flat back wall accent.
// =====================================================================

export function generateTransom(hull: BoatHull): BoatPart[] {
  const draft = hull.draftEq;
  const freeboard = hull.freeboard;
  const L = hull.length;
  const beam = hull.beam * 0.85;
  const transomHeight = freeboard + draft * 0.6;
  const cy = -draft * 0.6 + transomHeight * 0.5;
  const cx = -L * 0.5 + 0.04;
  const panel = createBox(0.04, transomHeight, beam, hull.hullColor, "transom");
  return [
    {
      mesh: panel,
      transform: ([qx, qy, qz]) => [cx + qx, cy + qy, qz],
    },
  ];
}
